import React, { useMemo } from "react";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import { CompanyLogo } from "../../../../../components/CompanyLogo";
import { useCompanyPageStore } from "../../../../../store/companyPageStore";
import {
  CompanyActionButtons,
  CompanyActionButtonsSkeleton,
} from "../../../components/CompanyActionButtons";
import { ProfileScoreChip } from "@/lib/scoring/components/ScoreChip";
import { getProfileScores } from "@/lib/scoring/scoringUtils";
import { useScoringStore } from "@/store/scoringStore";

export const CompanyHeaderSkeleton: React.FC = () => {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      <Stack direction="row" alignItems="center" gap={2}>
        <Skeleton variant="rounded" width={60} height={60} />
        <Box>
          <Skeleton variant="text" width={300} height={40} />
        </Box>
      </Stack>
      <CompanyActionButtonsSkeleton />
    </Stack>
  );
};

export const CompanyHeader: React.FC = () => {
  const company = useCompanyPageStore((state) => state.company);
  const companyLoading = useCompanyPageStore(
    (state) => state.loadingStates.company
  );
  const drawerMode = useCompanyPageStore((state) => state.drawerMode);
  const companyScores = useCompanyPageStore((state) => state.companyScores);

  const profiles = useScoringStore((s) => s.profiles);
  const activeProfileIds = useScoringStore((s) => s.activeProfileIds);
  const activeProfiles = useMemo(
    () =>
      activeProfileIds
        .map((id) => profiles.find((p) => p.id === id))
        .filter((p): p is (typeof profiles)[number] => p !== undefined),
    [profiles, activeProfileIds]
  );

  if (companyLoading || !company) {
    return <CompanyHeaderSkeleton />;
  }

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      <Stack direction="row" alignItems="center" gap={2}>
        <Box flex={1}>
          <CompanyLogo domain={company.url || ""} size={60} variant="rounded" />
        </Box>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Tooltip title={company.name || ""} placement="top-start">
            <Typography
              data-testid="company-detail-title"
              variant="h3"
              component="h1"
              fontWeight="bold"
              sx={{
                maxWidth: "400px",
                lineHeight: "1.2",
                overflow: "hidden",
                textOverflow: "ellipsis",
                wordBreak: "break-word",
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
              }}
            >
              {company.name}
            </Typography>
          </Tooltip>
          {activeProfiles.length > 0 && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              {activeProfiles.map((profile, idx) => {
                const { fitScoreNormalized, intentScoreNormalized } =
                  getProfileScores(companyScores, profile.id);
                const score = fitScoreNormalized ?? intentScoreNormalized;
                return (
                  <ProfileScoreChip
                    key={profile.id}
                    score={score}
                    profileName={profile.name}
                    profileOrder={idx + 1}
                  />
                );
              })}
            </Stack>
          )}
        </Stack>
      </Stack>

      {!drawerMode && (
        <Stack direction="row" spacing={1}>
          <CompanyActionButtons />
        </Stack>
      )}
    </Stack>
  );
};
